import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useFornecedoresListaViewModel } from "../../viewModels/fornecedores_lista_view_model";
import { Header } from "./components/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { GenerateFornecedoresListaColumnDefinition } from "./columns";
import { FornecedorLista } from "../../../domain/models/fornecedor";
import { GRID_CHECKBOX_SELECTION_FIELD, GridPinnedColumnFields } from "@mui/x-data-grid-pro";
import { AvaliacaoFornecedorModal } from "./components/avaliacao_fornecedor_modal";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";

import RefreshButton from "../../../../../common/components/refresh_button";
import AlertError from "../../../../../common/components/alert_error";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import DeleteDialog from "../../../../../common/components/delete_dialog";
import BottomBar from "../../../../../common/components/bottom_bar";

/**
 * FornecedoresLista
 */

export const FornecedoresLista = () => {
  const viewModel = useFornecedoresListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateFornecedoresListaColumnDefinition({
      onActionEditar: viewModel.handleEditar,
      onActionDeletar: viewModel.handleDeleteModal,
    });
  }, [
    viewModel.handleEditar,
    viewModel.handleDeleteModal,
  ]);

  const pinnedColumns = useMemo((): GridPinnedColumnFields => {
    return viewModel.fornecedores.length > 0 ?
      {
        left: [
          GRID_CHECKBOX_SELECTION_FIELD,
        ],
        right: [
          'idFornecedor',
        ],
      } : {
      };
  }, [
    viewModel.fornecedores,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[115px]">
                <PrimaryButton
                  buttonColor="yellow"
                  onMouseDown={e => {
                    viewModel.handleReportListaFornecedores();
                    e.preventDefault();
                  }}>
                  Relatório
                </PrimaryButton>
              </div>

              <div className="w-[115px]">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => {
                    if (e.button === 1) {
                      viewModel.handleEditar(null, true);
                    } else {
                      viewModel.handleEditar(null, e.ctrlKey);
                    }
                    e.preventDefault();
                  }}>
                  Novo Item
                </PrimaryButton>
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Compras
          </Link>
          <Typography className="text-primary-green">
            Fornecedores
          </Typography>
        </Breadcrumbs>

        <div className="flex flex-col gap-2">
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-3xl font-medium text-black dark:text-white">Fornecedores</h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>

            <div className="w-full justify-end flex gap-2">
            </div>
          </div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Header
                  razaoSocial={viewModel.filters.razaoSocial}
                  cnpj={viewModel.filters.cnpj}
                  cpf={viewModel.filters.cpf}
                  nomeFantasia={viewModel.filters.nomeFantasia}
                  centroCustos={viewModel.filters.centroCustos}
                  familias={viewModel.filters.familias}
                  atividades={viewModel.filters.atividades}
                  documento={viewModel.filters.documento}
                  tipoFornecedor={viewModel.filters.tipoFornecedor}
                  status={viewModel.filters.status}
                  avaliacaoCentroCusto={viewModel.filters.avaliacaoCentroCusto}
                  centroCustoDropdown={viewModel.centroCustoDropdown}
                  familiasDropdown={viewModel.familiasDropdown}
                  atividadesDropdown={viewModel.atividadesDropdown}
                  onRazaoSocialChange={viewModel.handleRazaoSocialChange}
                  onCnpjChange={viewModel.handleCnpjChange}
                  onCpfChange={viewModel.handleCpfChange}
                  onNomeFantasiaChange={viewModel.handleNomeFantasiaChange}
                  onCentroCustosChange={viewModel.handleCentroCustosChange}
                  onFamiliasChange={viewModel.handleFamiliasChange}
                  onAtividadesChange={viewModel.handleAtividadesChange}
                  onDocumentoChange={viewModel.handleDocumentoChange}
                  onTipoFornecedorChange={viewModel.handleTipoFornecedorChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onAvaliacaoCentroCustoChange={viewModel.handleAvaliacaoCentroCustoChange}
                  onClearFilters={viewModel.handleClearFilters}
                />

                <Stack spacing={0}>
                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                  <StyledDataGrid
                    tableName="fornecedores_lista"
                    disableColumnMenu={false}
                    getRowId={(row: FornecedorLista) => row.idFornecedor}
                    pinnedColumns={pinnedColumns}
                    checkboxSelection={true}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    disableRowSelectionOnClick={true}
                    rows={viewModel.fornecedores}
                    rowCount={viewModel.fornecedores.length}
                    rowSelectionModel={viewModel.rowSelectionModel}
                    getRowHeight={() => 'auto'}
                    onRowSelectionModelChange={viewModel.handleRowSelectionModelChange}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                </Stack>
              </>
            )
        }
      </div>

      {/**
        * BottomBar
        */}

      <BottomBar>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
          </div>

          <div className="flex flex-row gap-4">
            <div className="w-[225px]">
              <PrimaryButton
                onMouseDown={e => {
                  viewModel.handleAvaliacaoModalOpen(true);
                  e.preventDefault();
                }}>
                Nova Avaliação de Fornecedor
              </PrimaryButton>
            </div>
          </div>
        </div>
      </BottomBar>

      {/**
        * Modals
        */}

      <AvaliacaoFornecedorModal
        open={viewModel.avaliacaoFornecedorModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        rowSelectionModel={viewModel.rowSelectionModel}
        funcionarioInformation={viewModel.funcionarioInformation}
        fornecedoresDropdown={viewModel.fornecedoresDropdown}
        centroCustosDropdown={viewModel.centroCustoDropdown}
        funcionariosDropdown={viewModel.funcionariosDropdown}
        criteriosAvaliacaoFornecedor={viewModel.criteriosAvaliacaoFornecedor}
        onClose={() => viewModel.handleAvaliacaoModalOpen(false)}
        onSubmit={viewModel.handleAvaliacaoSubmit}
      />

      <DeleteDialog
        open={viewModel.fornecedorDeleteModal}
        saving={viewModel.saving}
        title='Você realmente deseja excluir este item?'
        onClose={() => viewModel.handleDeleteModal(null, false)}
        onDelete={() => viewModel.handleDeleteSubmit()}
      />

      <ReportViewerModal
        reportViewerState={viewModel.reportViewerState}
      />
    </>
  );
}